import type { FC } from "react";
import { Translate, useTranslation } from "@privacy/translations";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import * as FormDialog from "../../../shared/FormDialog";

const bodyStyles: SxProps<Theme> = {
  color: theme => theme.palette.text.secondary,
  fontSize: theme => theme.typography.pxToRem(14),
};

const Strong: FC<{ children: string }> = ({ children }) => (
  <Box component="strong" sx={{ fontWeight: 600 }}>
    {children}
  </Box>
);

const warningDialogKeys = {
  changeConnectedTenants: "RegistrationDrawer.ConnectedTenantsWarningDialog",
  copy: "RegistrationDrawer.CopyWarningDialog",
} as const;

export const ResponsibleLosesAccessDialog: FC<{
  context: keyof typeof warningDialogKeys;
  registrationName: string;
  responsibleName: string;
  unitsResponsibleCannotAccess: ReadonlyArray<string>;
  isSubmitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({
  context,
  registrationName,
  responsibleName,
  unitsResponsibleCannotAccess,
  isSubmitting,
  onConfirm,
  onCancel,
}) => {
  const { t, formatList } = useTranslation();
  const keys = warningDialogKeys[context];

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Title>{t(`${keys}.Header`)}</FormDialog.Title>
      <FormDialog.Form
        onSubmit={event => {
          event.preventDefault();
          onConfirm();
        }}
      >
        <FormDialog.Content>
          <Typography sx={bodyStyles}>
            <Translate translationKey={`${keys}.Body`} values={{ registration: <Strong>{registrationName}</Strong> }} />
          </Typography>
          <Stack component="ul" sx={{ mt: 2, mb: 0, pl: 3, gap: 1 }}>
            <Typography component="li" sx={bodyStyles}>
              {unitsResponsibleCannotAccess.length === 0 ? (
                t(`${keys}.ResponsibleLosesAccess`)
              ) : (
                <Translate
                  translationKey={`${keys}.ResponsibleLosesAccessInUnits`}
                  values={{
                    responsible: <Strong>{responsibleName}</Strong>,
                    units: <Strong>{formatList(unitsResponsibleCannotAccess)}</Strong>,
                  }}
                />
              )}
            </Typography>
            <Typography component="li" sx={bodyStyles}>
              {t(`${keys}.YouBecomeResponsible`)}
            </Typography>
            <Typography component="li" sx={bodyStyles}>
              {t(`${keys}.DelegateLater`)}
            </Typography>
          </Stack>
        </FormDialog.Content>
        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onCancel} />
          <FormDialog.SubmitButton isSubmitting={isSubmitting}>{t(`${keys}.SubmitButton`)}</FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
